// Mock leaderboard world — port of the design project's mockData.js.
// A single module-level engine instance is shared by the dashboard preview
// card and the full screen so both views show the same simulated board.
// Swapped out for the real API by the USE_MOCK flag in leaderboardService.ts.

import {LeaderboardSnapshot, Period} from './types';

// Deterministic seeded generator so the roster is stable across reloads.
function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const NAMES = [
  'KnightRider64',
  'QueensGambit',
  'RookiePro',
  'BishopBlitz',
  'CaissaDreams',
  'ZugzwangZoe',
  'EnPassantEli',
  'TalDisciple',
  'PetrosianWall',
  'FischerHeat',
  'CarlsenJr',
  'BlitzBaron',
  'EndgameOwl',
  'PawnStormer',
  'SicilianDrag',
  'GrandPatzer',
  'TempoTian',
  'KingsIndianK',
  'BerlinDefender',
  'LondonSystem',
  'NajdorfNina',
  'FianchettoFox',
  'OpeningTheo',
  'RapidRaptor',
  'StalemateSam',
  'GambitGoblin',
  'CheckmateCat',
  'SwiftKnight',
];

export const MOCK_YOU_USERID = -1;

interface MockPlayer {
  userid: number;
  name: string;
  rating: number;
  you: boolean;
  wins: Record<Period, number>;
}

function buildPlayers(): MockPlayer[] {
  const rnd = makeRng(20260708);
  const skew = () => rnd() * rnd(); // bias toward smaller values → realistic long tail
  const players: MockPlayer[] = NAMES.map((name, i) => {
    const rating = 640 + Math.round(rnd() * 1520);
    const dW = 3 + Math.round(skew() * 26);
    const wW = 22 + Math.round(skew() * 120);
    return {
      userid: 1000 + i,
      name,
      rating,
      you: false,
      wins: {daily: dW, weekly: wW},
    };
  });
  players.push({
    userid: MOCK_YOU_USERID,
    name: 'You',
    rating: 862,
    you: true,
    wins: {daily: 3, weekly: 118},
  });
  return players;
}

function rankOrder(players: MockPlayer[], period: Period): MockPlayer[] {
  return [...players].sort(
    (a, b) =>
      b.wins[period] - a.wins[period] ||
      b.rating - a.rating ||
      a.userid - b.userid,
  );
}

type Listener = () => void;

class MockLeaderboardEngine {
  private players = buildPlayers();
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  // userids whose wins changed on the latest tick (drives the row flash).
  lastBumped = new Set<number>();

  // Let the caller brand the "you" row with the real signed-in identity.
  setYou(name?: string, rating?: number) {
    const you = this.players.find(p => p.you);
    if (!you) {
      return;
    }
    let changed = false;
    if (name && you.name !== name) {
      you.name = name;
      changed = true;
    }
    if (rating && rating > 0 && you.rating !== rating) {
      you.rating = rating;
      changed = true;
    }
    if (changed) {
      this.emit();
    }
  }

  snapshot(period: Period): LeaderboardSnapshot {
    const ordered = rankOrder(this.players, period);
    const entries = ordered.map((p, i) => ({
      userid: p.userid,
      rank: i + 1,
      name: p.name,
      rating: p.rating,
      wins: p.wins[period],
      you: p.you,
    }));
    const meRow = entries.find(e => e.you)!;
    return {
      period,
      entries,
      me: {
        rank: meRow.rank,
        wins: meRow.wins,
        rating: meRow.rating,
        name: meRow.name,
        inTop: meRow.rank <= 20,
      },
      totalPlayers: entries.length,
      leaderWins: entries[0]?.wins ?? 0,
    };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    if (!this.timer) {
      this.timer = setInterval(() => this.tick(), 2300);
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }

  // One simulated batch of finished games (1-2 winners), like the design.
  private tick() {
    const humans = this.players.filter(p => !p.you);
    const n = 1 + Math.floor(Math.random() * 2);
    this.lastBumped = new Set<number>();
    for (let k = 0; k < n; k++) {
      const p = humans[Math.floor(Math.random() * humans.length)];
      p.wins.daily += 1;
      p.wins.weekly += 1;
      this.lastBumped.add(p.userid);
    }
    this.emit();
  }

  private emit() {
    this.listeners.forEach(l => l());
  }
}

export const mockLeaderboardEngine = new MockLeaderboardEngine();
